import { useEffect, useState } from "react";
import {
  Activity, ShieldCheck, ShieldAlert, FileSignature, Send, RefreshCw, FlaskConical,
} from "lucide-react";
import api from "@/lib/api";
import { PageHeader } from "@/components/Common";
import { toast } from "sonner";

function StatusDot({ ok, label }) {
  return (
    <span className="inline-flex items-center gap-2 text-sm">
      <span className={`w-2.5 h-2.5 rounded-full ${ok ? "bg-[#507A59]" : "bg-[#A44A4A]"}`} />
      {label}
    </span>
  );
}

export default function SriDiagnostics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [test, setTest] = useState(null);
  const [testing, setTesting] = useState(false);

  const fetchDiag = async () => {
    setLoading(true);
    try {
      const r = await api.get("/sri/diagnostics");
      setData(r.data);
    } catch (e) { toast.error("No se pudo obtener el diagnóstico"); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchDiag(); }, []);

  const runTest = async () => {
    setTesting(true);
    setTest(null);
    try {
      const { data: t } = await api.post("/sri/test-comprobante");
      setTest(t);
      if (t.ok) toast.success("Comprobante de prueba aceptado por SRI");
      else toast.warning("SRI respondió, ver detalle");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error en prueba SRI");
    } finally { setTesting(false); }
  };

  return (
    <div data-testid="sri-diagnostics-page">
      <PageHeader
        title="Diagnóstico SRI"
        subtitle="Estado del entorno SRI, certificado digital y prueba de comprobantes."
        testid="sri-header"
        action={
          <button data-testid="sri-refresh-button" onClick={fetchDiag} disabled={loading} className="btn-secondary flex items-center gap-2">
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> {loading ? "Verificando..." : "Re-verificar"}
          </button>
        }
      />

      {!data ? (
        <div className="card-soft p-10 text-center text-[#5C6B64]">Verificando entorno SRI...</div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <div className="card-soft p-6" data-testid="sri-environment-card">
            <div className="flex items-start justify-between mb-4">
              <div>
                <p className="text-xs uppercase tracking-[0.22em] text-[#8A9A92]">Ambiente activo</p>
                <p className="font-heading text-2xl font-black mt-1">{data.ambiente}</p>
                <p className="text-xs font-mono text-[#5C6B64] mt-1">{data.host}</p>
              </div>
              <Activity className="w-6 h-6 text-[#84978F]" strokeWidth={1.7} />
            </div>
            <div className="space-y-2">
              <StatusDot ok={data.recepcion_wsdl?.ok} label={`WSDL Recepción · HTTP ${data.recepcion_wsdl?.http_code || "—"}`} />
              <StatusDot ok={data.autorizacion_wsdl?.ok} label={`WSDL Autorización · HTTP ${data.autorizacion_wsdl?.http_code || "—"}`} />
              <StatusDot ok={data.recepcion_probe?.ok} label={`WS Recepción: ${data.recepcion_probe?.message || data.recepcion_probe?.error || "—"}`} />
            </div>
          </div>

          <div className="card-soft p-6" data-testid="sri-certificate-card">
            <div className="flex items-start justify-between mb-4">
              <div>
                <p className="text-xs uppercase tracking-[0.22em] text-[#8A9A92]">Certificado digital</p>
                <p className="font-heading text-lg font-bold mt-1">
                  {data.certificate?.loaded ? "Cargado correctamente" : "No cargado"}
                </p>
              </div>
              {data.certificate?.loaded ? (
                <ShieldCheck className="w-6 h-6 text-[#507A59]" strokeWidth={1.7} />
              ) : (
                <ShieldAlert className="w-6 h-6 text-[#A44A4A]" strokeWidth={1.7} />
              )}
            </div>
            {data.certificate?.loaded ? (
              <div className="space-y-2 text-sm">
                <Row label="RUC en certificado"><span className="font-mono">{data.certificate.ruc_in_cert}</span></Row>
                <Row label="Coincide con Ajustes">
                  <StatusDot ok={data.ruc_match} label={data.ruc_match ? "Sí" : "No"} />
                </Row>
                <Row label="Vigencia">
                  <span className={data.certificate.expired ? "text-[#A44A4A] font-bold" :
                    (data.certificate.expiring_soon ? "text-[#8C4A35] font-bold" : "")}>
                    {data.certificate.days_remaining} días restantes
                  </span>
                </Row>
                <Row label="Válido hasta"><span className="text-xs">{new Date(data.certificate.valid_to).toLocaleDateString()}</span></Row>
                {data.ruc_warning && (
                  <p className="mt-3 p-3 bg-[#F3DADA] text-[#7A2E2E] text-xs rounded-md">{data.ruc_warning}</p>
                )}
              </div>
            ) : (
              <p className="text-sm text-[#5C6B64]">{data.certificate?.reason || "—"}</p>
            )}
          </div>

          <div className="card-soft p-6 lg:col-span-2" data-testid="sri-test-card">
            <div className="flex items-start justify-between mb-4">
              <div>
                <p className="text-xs uppercase tracking-[0.22em] text-[#8A9A92]">Prueba de comprobante</p>
                <p className="font-heading text-xl font-bold mt-1">Enviar comprobante ficticio al SRI</p>
                <p className="text-sm text-[#5C6B64] mt-1">No afecta la secuencia ni guarda nada en el sistema. Útil para verificar firma + envío sin compromiso.</p>
              </div>
              <button data-testid="sri-test-button" onClick={runTest} disabled={testing}
                className="btn-primary flex items-center gap-2 whitespace-nowrap">
                <FlaskConical className="w-4 h-4" /> {testing ? "Enviando..." : "Lanzar prueba"}
              </button>
            </div>
            {test && (
              <div className="mt-4 p-4 rounded-lg border" style={{
                background: test.ok ? "#E4EFE6" : "#F6E5DD",
                borderColor: test.ok ? "#507A59" : "#C27A62",
              }}>
                <p className="font-heading font-bold mb-2">
                  {test.ok ? "✓ SRI aceptó el comprobante" : `Paso: ${test.step}`}
                </p>
                <div className="text-sm space-y-1">
                  {test.access_key && <Row label="Clave de acceso"><span className="font-mono text-xs break-all">{test.access_key}</span></Row>}
                  {test.recepcion_status && <Row label="Recepción"><span>{test.recepcion_status} — {test.recepcion_message}</span></Row>}
                  {test.autorizacion_status && <Row label="Autorización"><span>{test.autorizacion_status} — {test.autorizacion_message}</span></Row>}
                  {test.authorization_number && <Row label="N° autorización"><span className="font-mono text-xs">{test.authorization_number}</span></Row>}
                  {test.message && <Row label="Mensaje"><span>{test.message}</span></Row>}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div className="grid grid-cols-[160px_1fr] gap-2 items-start">
      <span className="text-[11px] uppercase tracking-[0.16em] text-[#8A9A92] pt-1">{label}</span>
      <div>{children}</div>
    </div>
  );
}
